import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { submitHours, getServiceTypes, getCommittees } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

export default function SubmitHoursPage() {
  const [serviceTypes, setServiceTypes] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [date, setDate] = useState(null);
  const [serviceType, setServiceType] = useState('');
  const [committee, setCommittee] = useState('');
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [stRes, commRes] = await Promise.all([getServiceTypes(), getCommittees()]);
        setServiceTypes(stRes.data.service_types || []);
        setCommittees(commRes.data.committees || []);
      } catch (e) {
        console.error('Failed to load data', e);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !serviceType || !hours) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      await submitHours({
        date: format(date, 'yyyy-MM-dd'),
        service_type: serviceType,
        hours: parseFloat(hours),
        description,
      });
      toast.success('Hours submitted successfully!');
      navigate('/my-hours');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit hours');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="animate-fade-in" data-testid="submit-hours-page">
      <div className="mb-8">
        <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Submit Hours</h1>
        <p className="text-[#5C5855] mt-1">Log your volunteer service hours</p>
      </div>

      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-white border border-[#E8E3DD] rounded-xl p-6 md:p-8 space-y-6">
          {/* Date */}
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Date *</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  data-testid="date-picker-trigger"
                  variant="outline"
                  className={`w-full justify-start text-left font-normal border-[#D1C9C0] bg-white hover:bg-[#F2EFEB] ${!date ? 'text-[#8A847F]' : 'text-[#2D2B2A]'}`}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-white" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setCalendarOpen(false); }}
                  initialFocus
                  data-testid="date-calendar"
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Service Type */}
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Service Type *</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger data-testid="service-type-select" className="border-[#D1C9C0] bg-white">
                <SelectValue placeholder="Select service type" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {serviceTypes.map((st) => (
                  <SelectItem key={st} value={st} data-testid={`service-type-${st.replace(/\s+/g, '-').toLowerCase()}`}>{st}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Committee */}
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Committee</Label>
            <Select value={committee} onValueChange={setCommittee}>
              <SelectTrigger data-testid="committee-select" className="border-[#D1C9C0] bg-white">
                <SelectValue placeholder="Select committee (optional)" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {committees.map((c) => (
                  <SelectItem key={c.committee_id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hours */}
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Hours *</Label>
            <div className="relative">
              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A847F]" />
              <Input
                data-testid="hours-input"
                type="number"
                step="0.25"
                min="0.25"
                max="24"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder="e.g., 2.5"
                className="pl-10 bg-white border-[#D1C9C0] focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]"
                required
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Description (optional)</Label>
            <Textarea
              data-testid="description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Briefly describe the work done..."
              rows={3}
              className="bg-white border-[#D1C9C0] focus:ring-2 focus:ring-[#D3735A]/50 focus:border-[#D3735A]"
            />
          </div>

          <Button
            data-testid="submit-hours-button"
            type="submit"
            disabled={submitting}
            className="w-full bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-6 py-3 font-semibold h-12"
          >
            {submitting ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Submit Hours</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
